const UserStatus = Object.freeze({
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    WAITLIST: 'waitlist',
    STATE_PENDING: 'state_pending',
    SUSPENDED: 'suspended',
    CANCELLED: 'cancelled',
    TERMINATED: 'terminated',
    INFUNNEL: 'infunnel',
    DELETED: 'Deleted',
    DELINQUENT: 'Delinquent',
    DEFAULT: 'Default',
    FAILED_OFAC: 'failed_ofac',
    FAILED_PEP: 'failed_pep',
    CLOSED: 'closed',
    RECONCILE: 'reconcile'
});

const UserBoostLevel = Object.freeze({
    LEVEL1: 'level-1',
    LEVEL2: 'level-2',
    LEVEL3: 'level-3',
    LEVEL4: 'level-4',
    LEVEL5: 'level-5',
    LEVELVIP: 'level-vip',
});

const SetupStatus = Object.freeze({
    EMAIL_VERIFICATION: 'verify-email',
    PHONE_VERIFICATION: 'phone-verification',
    DEPOSIT_AGREEMENTS: 'deposit-agreements',
    LINK_BANK_ACCOUNT: 'link-bank-account',
    CUSTOMER_INFO: 'customer-info',
    CUSTOMER_SSN: 'customer-ssn',
    KYC_PENDING: 'kyc-pending',
    KYC_IN_PROGRESS: 'kyc-in-progress',
    ADD_TO_CRB: 'add-to-crb',
    ADD_TO_GALILEO: 'add-to-galileo',
    SETUP_MFA: 'setup-mfa',
    COMPLETE: 'complete',
    INCOMPLETE: 'incomplete',
});

/** Pro business `status` values from the businesses API. */
const ProBusinessAccountStatus = Object.freeze({
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    DENIED: 'denied',
    FAILED_KYB: 'failed_kyb',
    EXPIRED_KYB: 'expired_kyb',
});

/** Pro business `setupStatus` values from the businesses API (KYB / onboarding). */
const ProBusinessSetupStatus = Object.freeze({
    PENDING: 'pending',
    KYB_PENDING: 'kyb-pending',
    KYB_IN_PROGRESS: 'kyb-in-progress',
    ADD_TO_CRB: 'add-to-crb',
    COMPLETE: 'complete',
});

// Transaction type mapping
const ActivityTypeMap = {
    AB: 'Allpoint Backout',
    AD: 'Withdrawal',
    AP: 'Allpoint Authorization',
    AS: 'Allpoint Settlement',
    AU: 'Mastercard Credit authorization',
    AX: 'Allpoint Authorization Expiration',
    BA: 'Allpoint Preauthorization Backout',
    BC: 'Discover Backout',
    BD: 'Maestro/Cirrus Backout',
    BO: 'Mastercard credit Backout',
    BP: 'Pulse Backout',
    BS: 'Star Authorization Backout',
    BV: 'Visa Authorization Backout',
    CX: 'Discover Expired Authorization',
    DA: 'Denied Authorization; Non-financial Transaction.',
    DB: 'Maestro/Cirrus Authorization',
    DC: 'Discover Authorization',
    DD: 'Denied Maestro Authorization',
    DX: 'Maestro/Cirrus Expired Authorization',
    EX: 'Mastercard Credit Expired Authorization',
    FE: 'Fee',
    IS: 'Visa Interlink Settlement',
    MP: 'Star MoneyPass Settlement',
    OB: 'Umbrella Reserve Release/Backout',
    OE: 'Umbrella Reserve Release/Expiration',
    OR: 'Umbrella Reserve',
    OT: 'Umbrella Take',
    PB: 'Mastercard Credit Preauthorization Backout',
    PE: 'Pending Payment',
    PL: 'Visa Plus Settlement',
    PM: 'Payment',
    PR: 'Star Presto Settlement',
    PS: 'Star Preauthorization Backout',
    PU: 'Pulse Authorization',
    PV: 'Visa Preauthorization Backout',
    PX: 'Pulse Expired Authorization',
    SC: 'Discover Settlement',
    SD: 'Maestro/Cirrus Settlement',
    SE: 'Mastercard Credit Settlement',
    SP: 'Pulse Settlement',
    SS: 'Star Settlement',
    ST: 'Star Authorization',
    SX: 'Star Expired Authorization',
    TE: 'Transaction Expired',
    TH: 'Transaction Hold',
    VI: 'Visa Authorization',
    VS: 'Visa Settlement',
    VX: 'Visa Expired Authorization',
};

// HubspotUser
const HubspotAccountStatus = Object.freeze({
    ACCOUNT_ACTIVE: 'Account Active',
    DORMANT: 'Dormant',
    WAITLIST: 'Waitlist',
    STATE_PENDING: 'StatePending',
    INACTIVE: 'Inactive',
    DELETED: 'Deleted',
    SUSPENDED: 'Suspended',
    DELINQUENT: 'Delinquent',
    DEFAULT: 'Default',
    CLOSED: 'Closed',
});

const SettingLimitLevelType = Object.freeze({
    HALF_NOTIFY: 'Warning Notify',
    QUARTER_NOTIFY: 'Danger Notify',
    BLOCK_NOTIFY: 'Block Boost Limit',
});

const UserRole = Object.freeze({
    ADMIN: 'admin',
    SUPPORT: 'support',
    USER: 'user',
});

const BoostSpeedType = Object.freeze({
    ACH_5_DAYS: 'ach_5_days',
    ACH_3_DAYS: 'ach_3_days',
    ACH_1_DAY: 'ach_1_day',
    ACH_INSTANT: 'ach_instant',
});

const TransactionLimitProvider = Object.freeze({
    CRB: 'crb',
    ZEROHASH: 'zerohash',
});

const TransactionMethod = Object.freeze({
    ACH: 'ach',
    // Limits-keying only: this is how the per-advance amount cap is stored, so it appears in the
    // Transaction Limits and tier-override screens. It is NOT a deposit method a client can ask
    // for. An ACH Advance is an ACH deposit EDGE fronts after hours, and advance-ness is derived
    // server-side from the banking window plus the user whitelist.
    ACH_ADVANCE: 'ach_advance',
    RTP: 'rtp',
    INSTANT: 'instant',
});

// Mirror of backend TransactionProviderMethodAllowList — keep in sync.
// Order matches the backend list, since both screens render methods in this order.
const TransactionProviderMethodAllowList = Object.freeze({
    [TransactionLimitProvider.CRB]: [
        TransactionMethod.ACH,
        TransactionMethod.ACH_ADVANCE,
        TransactionMethod.RTP,
    ],
    [TransactionLimitProvider.ZEROHASH]: [TransactionMethod.INSTANT],
});

const getValidMethodsForProvider = (provider) =>
    TransactionProviderMethodAllowList[provider] || [];

const isValidProviderMethodPair = (provider, method) =>
    getValidMethodsForProvider(provider).includes(method);

const validProviderMethodPairs = Object.entries(TransactionProviderMethodAllowList)
    .flatMap(([provider, methods]) => methods.map((method) => ({ provider, method })));

// PaymentHistory
const PaymentHistoryType = Object.freeze({
    MATCHED_DEPOSIT: 'matched_deposit',
    BOOST: 'boost',
    AUTHORIZATION: 'authorization',
    REFUND: 'refund',
    DEPOSIT: 'deposit',
});

const AdjustmentModel = Object.freeze({
    ADJUSTMENT: 'GalileoAdjustment',
    PAYMENT: 'GalileoPayment',
});

// Galileo
const GalileoAccountStatus = Object.freeze({
    CANCELED: 'C',
    DISABLED: 'D',
    FAILED_ID_VERIFICATION: 'F',
    SUSPENDED_NEVER_ACTIVATED: 'J',
    SUSPENDED: 'K',
    MOVED_PROGRAM: 'M',
    ACTIVE: 'N',
    PASSED_ID_VERIFICATION: 'P',
    DELINQUENT: 'Q',
    CHARGED_OFF: 'R',
    ID_VERIFICATION_IN_PROCESS: 'T',
    UPGRADED: 'U',
    APPLICATION_SUBMITTED: 'V',
    WAITING_TO_BE_PROCESSED: 'W',
    CANCELED_WITHOUT_REFUND: 'Z',
});

const GalileoCardStatus = Object.freeze({
    LOST_CARD_WAITING_FOR_SUFFICIENT_FUNDS: 'A',
    BLOCKED: 'B',
    CANCELED: 'C',
    DISABLED: 'D',
    LOST_CARD: 'L',
    ACTIVE: 'N',
    OPERATIONS_HOLD: 'O',
    DELINQUENT: 'Q',
    CHARGED_OFF: 'R',
    STOLEN_CARD: 'S',
    VOIDED: 'V',
    WAITING_FOR_PAYMENT: 'W',
    SET_TO_EMBOSS: 'X',
    READY_TO_ACTIVATE: 'Y',
    CANCELED_WITHOUT_REFUND: 'Z',
});

const GalileoAccountStatusText = Object.freeze({
    C: 'CANCELED',
    D: 'DISABLED',
    F: 'FAILED_ID_VERIFICATION',
    J: 'SUSPENDED_NEVER_ACTIVATED',
    K: 'SUSPENDED',
    M: 'MOVED_PROGRAM',
    N: 'ACTIVE',
    P: 'PASSED_ID_VERIFICATION',
    Q: 'DELINQUENT',
    R: 'CHARGED_OFF',
    T: 'ID_VERIFICATION_IN_PROCESS',
    U: 'UPGRADED',
    V: 'APPLICATION_SUBMITTED',
    W: 'WAITING_TO_BE_PROCESSED',
    Z: 'CANCELED_WITHOUT_REFUND',
});

const GalileoCardStatusText = Object.freeze({
    A: 'LOST_CARD_WAITING_FOR_SUFFICIENT_FUNDS',
    B: 'BLOCKED',
    C: 'CANCELED',
    D: 'DISABLED',
    L: 'LOST_CARD',
    N: 'ACTIVE',
    O: 'OPERATIONS_HOLD',
    Q: 'DELINQUENT',
    R: 'CHARGED_OFF',
    S: 'STOLEN_CARD',
    V: 'VOIDED',
    W: 'WAITING_FOR_PAYMENT',
    X: 'SET_TO_EMBOSS',
    Y: 'READY_TO_ACTIVATE',
    Z: 'CANCELED_WITHOUT_REFUND',
});

const GalileoModifyAction = Object.freeze({
    ACTIVE_CARD: 7,
    DISABLE_CARD: 23,
    FREEZE_CARD: 17,
    UNFREEZE_CARD: 18,
    DISABLE_ACCOUNT: 10,
    ACTIVE_ACCOUNT: 11,
    SUSPEND_ACCOUNT: 24,
    CANCEL_CARD_ACCOUNT: 13,

    // Lost and Stolen card actions (no auto-replacement)
    LOST_CARD: 8,
    STOLEN_CARD: 9,
});

// RepaymentIntent
const RepaymentIntentStatus = Object.freeze({
    IN_SCHEDULE: 'in-schedule',
    ON_TIME: 'on-time',
    FAILED: 'failed',
    PAID: 'paid',
    PAID_EARLY: 'paid-early',
    PAID_LATE: 'paid-late',
    PENDING: 'pending',
});

const TransactionType = Object.freeze({
    MATCHED_DEPOSIT: 'matched_deposit',
    BOOST: 'boost',
    AUTHORIZATION: 'authorization',
    REFUND: 'refund',
    DEPOSIT: 'deposit',
    WITHDRAWAL: 'withdrawal',
    CARD_DEPOSIT: 'card_deposit',
    CREDIT: 'credit',
    DEBIT: 'debit',
});

const AchRequestStatus = Object.freeze({
    CREATED: 'Created',
    SENT: 'Sent',
    COMPLETE: 'Complete',
    RETURNED: 'Returned',
    QUEUED: 'Queued',
    RELEASED: 'Released',
    RECEIVED: 'Received',
    CANCELLED: 'Cancelled',
});

// ACH Request EdgeType
const AchEdgeType = Object.freeze({
    ACH_SWEEP: 'ach-sweep',
    MATCHED_DEPOSIT: 'matched-deposit',
    DEPOSIT: 'deposit',
    WITHDRAWAL: 'withdraw',
});

// ACH Return Codes
const AchReturnCode = Object.freeze({
    R01: 'Insufficient Funds',
    R02: 'Account closed',
    R03: 'No account or unable to locate account',
    R04: 'Invalid account number',
    R05: 'Unauthorized debit to consumer account',
    R07: 'Authorization revoked by customer',
    R08: 'Payment stopped or stop payment on item',
    R09: 'Uncollected funds',
    R10: 'Customer advises not authorized',
    R11: 'Customer Advises Entry Not in Accordance with the Terms of the Authorization',
    R16: 'Account frozen',
    R17: 'File record edit criteria',
    R20: 'Non-transaction account',
    R23: 'Credit entry refused by receiver',
    R29: 'Corporate customer advises not authorized',
    R51: 'Ineligible / Improper Item Related to RCK',
});

// ACH Return Code Map for easy lookup
const AchReturnCodeMap = {
    R01: 'Insufficient Funds',
    R02: 'Account closed',
    R03: 'No account or unable to locate account',
    R04: 'Invalid account number',
    R05: 'Unauthorized debit to consumer account',
    R07: 'Authorization revoked by customer',
    R08: 'Payment stopped or stop payment on item',
    R09: 'Uncollected funds',
    R10: 'Customer advises not authorized',
    R11: 'Customer Advises Entry Not in Accordance with the Terms of the Authorization',
    R16: 'Account frozen',
    R17: 'File record edit criteria',
    R20: 'Non-transaction account',
    R23: 'Credit entry refused by receiver',
    R29: 'Corporate customer advises not authorized',
    R51: 'Ineligible / Improper Item Related to RCK',
};

export {
    UserStatus,
    UserBoostLevel,
    UserRole,
    HubspotAccountStatus,
    SettingLimitLevelType,
    BoostSpeedType,
    TransactionLimitProvider,
    TransactionMethod,
    TransactionProviderMethodAllowList,
    getValidMethodsForProvider,
    isValidProviderMethodPair,
    validProviderMethodPairs,
    PaymentHistoryType,
    AdjustmentModel,
    GalileoAccountStatus,
    GalileoCardStatus,
    GalileoAccountStatusText,
    GalileoCardStatusText,
    GalileoModifyAction,
    RepaymentIntentStatus,
    ProBusinessAccountStatus,
    SetupStatus,
    ProBusinessSetupStatus,
    TransactionType,
    AchRequestStatus,
    AchEdgeType,
    AchReturnCode,
    AchReturnCodeMap,
    ActivityTypeMap,
};

export default {
    UserStatus,
    UserBoostLevel,
    UserRole,
    HubspotAccountStatus,
    SettingLimitLevelType,
    BoostSpeedType,
    TransactionLimitProvider,
    TransactionMethod,
    TransactionProviderMethodAllowList,
    getValidMethodsForProvider,
    isValidProviderMethodPair,
    validProviderMethodPairs,
    PaymentHistoryType,
    AdjustmentModel,
    GalileoAccountStatus,
    GalileoCardStatus,
    GalileoAccountStatusText,
    GalileoCardStatusText,
    GalileoModifyAction,
    RepaymentIntentStatus,
    ProBusinessAccountStatus,
    SetupStatus,
    ProBusinessSetupStatus,
    TransactionType,
    AchRequestStatus,
    AchEdgeType,
    AchReturnCode,
    AchReturnCodeMap,
    ActivityTypeMap,
};
